import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard, Users, Building2, Send, FileText,
  FormInput, Settings, LogOut, Mail, ShieldCheck, BarChart3, TrendingUp
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';

const ROLE_LABELS = { super_admin: 'Super Admin', admin: 'Admin', agent: 'Agent' };
const ROLE_COLORS = { super_admin: 'text-[#FFB800]', admin: 'text-[#007AFF]', agent: 'text-[#B0B0B0]' };

export const Sidebar = () => {
  const { user, logout, hasRole } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', minRole: 'agent' },
    { to: '/contacts', icon: Users, label: 'Contacts', minRole: 'agent' },
    { to: '/businesses', icon: Building2, label: 'Businesses', minRole: 'agent' },
    { to: '/campaigns', icon: Send, label: 'Campaigns', minRole: 'agent' },
    { to: '/analytics', icon: TrendingUp, label: 'Analytics', minRole: 'admin' },
    { to: '/templates', icon: FileText, label: 'Templates', minRole: 'agent' },
    { to: '/lists', icon: Mail, label: 'Mailing Lists', minRole: 'admin' },
    { to: '/lead-capture', icon: FormInput, label: 'Lead Capture', minRole: 'admin' },
    { to: '/admin', icon: ShieldCheck, label: 'Admin Panel', minRole: 'admin' },
    { to: '/reports', icon: BarChart3, label: 'Reports', minRole: 'super_admin' },
    { to: '/settings', icon: Settings, label: 'Settings', minRole: 'agent' },
  ];

  return (
    <aside data-testid="sidebar" className="w-64 h-screen sticky top-0 bg-[#0A0A0A] border-r border-[rgba(255,255,255,0.12)] flex flex-col">
      <div className="p-6 border-b border-[rgba(255,255,255,0.12)]">
        <h1 className="text-xl font-bold tracking-tight text-white" style={{ fontFamily: 'Manrope' }}>
          <span className="text-[#00FF99]">LCBSE</span> CRM
        </h1>
        <p className="text-xs text-[#808080] mt-1">Life Can Be So Easy</p>
      </div>

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navItems.filter(item => hasRole(item.minRole)).map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-200 ${
                isActive
                  ? 'bg-[#00FF99]/10 text-[#00FF99] font-medium'
                  : 'text-[#B0B0B0] hover:text-white hover:bg-[#1A1A1A]'
              }`
            }
          >
            <Icon size={18} strokeWidth={1.5} />
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="p-4 border-t border-[rgba(255,255,255,0.12)]">
        <div className="flex items-center gap-3 mb-3">
          <Avatar className="h-8 w-8">
            <AvatarImage src={user?.picture} />
            <AvatarFallback className="bg-[#1A1A1A] text-[#00FF99] text-xs">
              {user?.name?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white truncate">{user?.name || 'User'}</p>
            <p className={`text-xs ${ROLE_COLORS[user?.role] || 'text-[#808080]'}`}>
              {ROLE_LABELS[user?.role] || user?.role}
            </p>
          </div>
        </div>
        <button onClick={handleLogout} data-testid="logout-button"
          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-[#B0B0B0] hover:text-[#FF3B30] hover:bg-[#1A1A1A] rounded-md transition-colors duration-200">
          <LogOut size={16} strokeWidth={1.5} /> Log out
        </button>
      </div>
    </aside>
  );
};
